import {gql} from '@apollo/client';
import React, {Component} from 'react';
import isEqual from 'react-fast-compare';

import {assert} from 'Asserts';
import {SavingState} from 'constants/RegistryConstants';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {SchemaFieldEdge} from 'schemas/fields/SchemaFieldConstants';
import * as TagSchemaActions from 'tag-schema/TagSchemaActions';
import {useTagSchemaSavingState} from 'tag-schema/TagSchemaClientState';
import {ClientSchemaFieldPath} from 'tag-schema/TagSchemaDAO';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';

import {
  TagSchemaHoistedFieldTableQuery,
  TagSchemaHoistedFieldTableQueryVariables,
} from './__generated/TagSchemaHoistedFieldTableQuery';
import FieldTableHeader from './FieldTableHeader';
import HoistedFieldTableRow from './HoistedFieldTableRow';

type HoistedFieldTableTagSchemaGraphQL = ExtractByTypename<
  TagSchemaHoistedFieldTableQuery['tagSchema'],
  'TagSchemaNode'
>;

/** @package */
export interface QueryingHoistedFieldTableProps {
  isDisabled: boolean;
  tagSchemaId: string;
}

/** @package */
export interface HoistedFieldTableProps extends QueryingHoistedFieldTableProps {
  hoistedTagSchemaGraphs: HoistedFieldTableTagSchemaGraphQL['hoistedTagSchemaGraphsJson'];
  targetSchemaToPaths: HoistedFieldTableTagSchemaGraphQL['hoistedTagSchemaOrderedPathOptionsJson'];
  refresh: () => void;
}

interface HoistedFieldTableState {
  newHoistedTagSchemaGraphData: {
    orderedPath: Array<SchemaFieldEdge> | null;
    hoistedField: SchemaFieldEdge | null;
  } | null;
}

/** @package */
export class HoistedFieldTable extends Component<HoistedFieldTableProps, HoistedFieldTableState> {
  static defaultProps = {
    isDisabled: false,
  };

  override state: HoistedFieldTableState = {
    newHoistedTagSchemaGraphData: null,
  };

  override componentDidUpdate(prevProps: HoistedFieldTableProps): void {
    if (!isEqual(prevProps.hoistedTagSchemaGraphs, this.props.hoistedTagSchemaGraphs)) {
      this.setState({newHoistedTagSchemaGraphData: null});
    }
  }

  private shouldDisableAddField(): boolean {
    return this.state.newHoistedTagSchemaGraphData != null;
  }

  private handleAddField = (): void => {
    if (this.shouldDisableAddField()) {
      return;
    }
    this.setState({newHoistedTagSchemaGraphData: {orderedPath: null, hoistedField: null}});
  };

  private handleCompleteAddField = (): void => {
    const {orderedPath, hoistedField} = this.state.newHoistedTagSchemaGraphData!;
    assert(
      orderedPath != null && hoistedField != null,
      'Attempted to add new hoisted field with incomplete information.'
    );
    const graph: ClientSchemaFieldPath = {edges: orderedPath.concat(hoistedField)};
    TagSchemaActions.setHoistedFields(
      this.props.tagSchemaId,
      [...this.props.hoistedTagSchemaGraphs, graph],
      this.props.refresh
    );
  };

  private handleRemoveField = (hoistedFieldGraphId: number): void => {
    assert(
      this.props.hoistedTagSchemaGraphs.some((g) => g.id === hoistedFieldGraphId),
      'Attempted to remove hoisted field that could not be found.'
    );
    TagSchemaActions.setHoistedFields(
      this.props.tagSchemaId,
      this.props.hoistedTagSchemaGraphs.filter((g) => g.id !== hoistedFieldGraphId),
      this.props.refresh
    );
  };

  private handleEditNewRow = (orderedPath: Array<SchemaFieldEdge>, hoistedField: SchemaFieldEdge): void => {
    let newHoistedField: SchemaFieldEdge | null = hoistedField;
    if (!isEqual(this.state.newHoistedTagSchemaGraphData?.orderedPath, orderedPath)) {
      newHoistedField = null;
    }
    this.setState({
      newHoistedTagSchemaGraphData: {
        orderedPath,
        hoistedField: newHoistedField,
      },
    });
  };

  override render(): JSX.Element {
    return (
      <div className="simpleDivTable simpleDivTable--wide">
        <FieldTableHeader
          isDisabled={this.props.isDisabled}
          onAddField={this.shouldDisableAddField() ? null : this.handleAddField}
          title="Hoisted fields"
        />
        <div className="simpleDivTable-header">
          <div className="simpleDivTable-headerCell">Entity schema</div>
          <div className="simpleDivTable-headerCell">Field name</div>
        </div>
        {this.props.hoistedTagSchemaGraphs.map((graph) => {
          const orderedPath = graph.edges.slice(0, -1);
          const hoistedField = graph.edges.slice(-1)[0];
          return (
            <HoistedFieldTableRow
              key={graph.id}
              hoistedField={hoistedField}
              orderedPath={orderedPath}
              isDisabled={this.state.newHoistedTagSchemaGraphData != null || this.props.isDisabled}
              onDelete={() => {
                this.handleRemoveField(graph.id);
              }}
            />
          );
        })}
        {this.state.newHoistedTagSchemaGraphData && (
          <HoistedFieldTableRow
            hoistedField={this.state.newHoistedTagSchemaGraphData.hoistedField}
            isBeingCreated={true}
            orderedPath={this.state.newHoistedTagSchemaGraphData.orderedPath}
            targetSchemaToPaths={this.props.targetSchemaToPaths}
            onComplete={this.handleCompleteAddField}
            onDelete={() => {
              this.setState({newHoistedTagSchemaGraphData: null});
            }}
            onEdit={this.handleEditNewRow}
          />
        )}
        {!this.state.newHoistedTagSchemaGraphData && !this.props.hoistedTagSchemaGraphs.length && (
          <div className="simpleDivTable-row">
            <div className="simpleDivTable-cell simpleDivTable-cell--emptyMessage">
              This schema does not have any hoisted fields
            </div>
          </div>
        )}
      </div>
    );
  }
}

/** @package */
export const TAG_SCHEMA_HOISTED_FIELD_TABLE_QUERY = gql`
  query TagSchemaHoistedFieldTableQuery($id: StrictString!) @skipAudit @engComponent(name: REGISTRY) {
    tagSchema(id: $id) {
      ... on TagSchemaNode {
        id
        hoistedTagSchemaGraphsJson
        hoistedTagSchemaOrderedPathOptionsJson
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

interface TagSchemaHoistedFieldTableQueryData {
  tagSchema: HoistedFieldTableTagSchemaGraphQL;
  refresh: () => void;
}

function useTagSchemaHoistedFieldTableQuery(tagSchemaId: string): TagSchemaHoistedFieldTableQueryData | null {
  const result = useBenchlingQuery<TagSchemaHoistedFieldTableQuery, TagSchemaHoistedFieldTableQueryVariables>(
    TAG_SCHEMA_HOISTED_FIELD_TABLE_QUERY,
    {variables: {id: tagSchemaId}}
  );

  if (result.type !== QueryResultType.SUCCESS) {
    return null;
  } else {
    const tagSchema = result.data.tagSchema;
    if (isInaccessible(tagSchema)) {
      return null;
    } else {
      const refresh = (): void => {
        result.refetch({id: tagSchemaId});
      };
      return {tagSchema, refresh};
    }
  }
}

export default function QueryingHoistedFieldTable({
  tagSchemaId,
  isDisabled,
}: QueryingHoistedFieldTableProps): JSX.Element {
  const result = useTagSchemaHoistedFieldTableQuery(tagSchemaId);
  const [savingState] = useTagSchemaSavingState();

  if (result == null || savingState === SavingState.PENDING) {
    return <CenteredHorizontalThrobber />;
  } else {
    const {tagSchema, refresh} = result;
    return (
      <HoistedFieldTable
        tagSchemaId={tagSchemaId}
        hoistedTagSchemaGraphs={tagSchema.hoistedTagSchemaGraphsJson}
        targetSchemaToPaths={tagSchema.hoistedTagSchemaOrderedPathOptionsJson}
        isDisabled={isDisabled}
        refresh={refresh}
      />
    );
  }
}
